import * as joint from '@joint/core';
import { Node } from './node-base';

// Shared inspection/mutation loader: both paths use the same registered cell classes and entity
// attribution without editor compatibility cleanup, keeping snapshot cellIds addressable.
//
// Callers must register the shape modules they need before deserialization.

// Entity identity and visual metadata shared by inspection and mutation requests. Nodes persist
// ids only; metadata is resolved from this catalog. The mutation path uses isExternal to validate
// linked Third Party creation; inspection uses it when joining entity metadata.
export interface EntityCatalogEntry {
    entityId: string;
    label: string;
    shape: string;
    countryCode: string;
    isExternal?: boolean;
}

export function createGraph(graphJson: string): joint.dia.Graph {
    const graph = new joint.dia.Graph({}, { cellNamespace: joint.shapes });
    const graphData = graphJson && graphJson.trim().length > 0
        ? JSON.parse(graphJson)
        : { cells: [] };
    graph.fromJSON(graphData);
    backfillLinkEntityAttribution(graph);
    return graph;
}

// Backfill only unambiguous single-entity endpoints; multi-entity endpoints remain unattributed.
export function backfillLinkEntityAttribution(graph: joint.dia.Graph): void {
    for (const link of graph.getLinks()) {
        for (const [end, prop] of [['source', 'sourceEntityId'], ['target', 'targetEntityId']] as const) {
            if (link.prop(prop)) {
                continue;
            }
            const endId = getLinkEndId(link, end);
            const element = endId ? graph.getCell(endId) : undefined;
            if (element instanceof Node
                && Array.isArray(element.entityIds)
                && element.entityIds.length === 1) {
                link.prop(prop, element.entityIds[0]);
            }
        }
    }
}

export function getLinkEndId(link: joint.dia.Link, end: 'source' | 'target'): string | undefined {
    const endDefinition = link.get(end);
    if (!endDefinition || typeof endDefinition !== 'object') {
        return undefined;
    }

    const id = (endDefinition as { id?: unknown }).id;
    return typeof id === 'string' || typeof id === 'number'
        ? String(id)
        : undefined;
}
